import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { SpacePerformanceReportSummary } from '../../api/Rest/space';
import {
  FolderBreadcrumbText,
  LoadingSpinner,
  StationCellProps,
  StationCellType,
} from '../../components/Molecules';
import {
  GameTypeHelpPopup,
  GameTypeHelpType,
  stringToGameTypeHelpType,
} from '../../components/Popups';
import { GameListTemplateV2 } from '../../components/Templates';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { GameListsType } from '../../models/SessionStore';
import { PerformanceGame } from '../../models/SpaceStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getTimeString } from '../../utils/date';
import { getPeriod, PeriodType } from '../../utils/period';
import { getStarsByStar } from '../../utils/record';

type State = {
  performanceData: SpacePerformanceReportSummary | undefined;
  isLoading: boolean;
  isFinishedFetchSpace: boolean;
};

const { fetchSpace } = useSpaceStore.getState();

export const PerformanceReportGameList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { showPopup, hidePopup } = usePopup();
  const { classroomId } = useParams();

  const query = new URLSearchParams(location.search);
  const performance = query.get('performance');
  const periodType = query.get('period') as PeriodType;
  const navigationState = location.state as {
    withBackState: boolean;
  } | null;

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const partitionId = useSessionStore((store) => store.partitionId);

  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );
  const webBoosterType = userAccount?.settings.webBoosterType;

  const [state, setState] = useState<State>({
    performanceData: undefined,
    isLoading: true,
    isFinishedFetchSpace: false,
  });

  useRedirectEntry();

  const currentDate = useMemo(() => new Date(), []);

  const period = useMemo(() => {
    return getPeriod(currentDate, periodType, i18n.language);
  }, [currentDate, i18n.language, periodType]);

  useEffect(() => {
    if (!period || performance === null) {
      navigate(`/club/${classroomId}/performance-report`);
    }
  }, [classroomId, navigate, performance, period]);

  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId, spaceListFieldName]);

  const classroom = useSpaceStore(
    (store) => store.getSpace(classroomId ?? ''),
    shallow,
  );

  useRedirectHome(state.isFinishedFetchSpace && !classroom);

  // MARK: Get Performance Games
  const performanceGamesFieldName = `${activeUserId}_${webBoosterType}_${partitionId}_${periodType}_${performance}`;
  const performanceGamesField = useSpaceStoreListField(
    'performanceGames',
    performanceGamesFieldName,
  );

  const fetchPerformanceGames = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchPerformanceReportGames(
        {
          fieldName: performanceGamesFieldName,
          classroomId: classroomId ?? '',
          webBoosterType: webBoosterType,
          partitionId:
            partitionId === `${webBoosterType}-${FolderBreadcrumbText.All}`
              ? undefined
              : partitionId ?? undefined,
          from: period?.from?.getTime().toString(),
          to: period?.to?.getTime().toString(),
        },
        cursor,
      ),
    [
      performanceGamesFieldName,
      classroomId,
      webBoosterType,
      partitionId,
      period?.from,
      period?.to,
    ],
  );

  const {
    fetchMore: fetchMorePerformanceGames,
    refresh: refreshPerformanceGames,
    hasCursor,
  } = useFetchList(performanceGamesField, fetchPerformanceGames);

  useEffect(() => {
    if (classroomId) {
      refreshPerformanceGames()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isLoading: false }));
        });
    }
  }, [refreshPerformanceGames, classroomId]);

  const loadMoreItems = useCallback(() => {
    fetchMorePerformanceGames().catch(console.error);
  }, [fetchMorePerformanceGames]);

  const performanceGames = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getPerformanceReportGames(performanceGamesFieldName);
  }, arrayEqual(shallow));

  const onClickInfo = useCallback(
    (type: GameTypeHelpType) => {
      showPopup(<GameTypeHelpPopup type={type} onPrimary={hidePopup} />);
    },
    [hidePopup, showPopup],
  );

  const stationsData = useMemo<StationCellProps[]>(() => {
    const performanceGameIds = new Set();
    const gamePerformances: [
      PerformanceGame[],
      PerformanceGame[],
      PerformanceGame[],
      PerformanceGame[],
      PerformanceGame[],
      PerformanceGame[],
    ] = [[], [], [], [], [], []];

    performanceGames.forEach((performanceGame) => {
      if (!performanceGameIds.has(performanceGame.game.gameId)) {
        performanceGameIds.add(performanceGame.game.gameId);
        if (performanceGame.gameRecord.stars) {
          gamePerformances[performanceGame.gameRecord.stars]?.push(
            performanceGame,
          );
        }
      }
    });

    const level = Number(performance);

    const selectedPerformanceGames = gamePerformances[level];

    if (selectedPerformanceGames && userProfile) {
      const gameLinkList = selectedPerformanceGames.map(
        (performanceGame) =>
          `/club/${performanceGame.gameRecord.classroomId}/journey/${performanceGame.gameRecord.gameSetId}/game/${performanceGame.gameRecord.gameId}`,
      );
      return selectedPerformanceGames.map((performanceGame) => {
        const gameItems = performanceGame.gameCollection.getPartItemArray(
          (item) => item.game,
        );
        const gameItem = gameItems.find(
          (item) => item.game?.gameId === performanceGame.game.gameId,
        );
        return {
          type: StationCellType.Game,
          props: {
            game: performanceGame.game,
            stationId: undefined, // stationStyle?.stations.game ?? StationGameItemNo.s1,
            name: '',
            index: -1,
            iconKey: gameItem?.icon?.tag ?? '',
            playCount: performanceGame.gameStatistics.playCount ?? 0,
            averageScore: performanceGame.gameStatistics.averageScore ?? 0,
            userProfile,
            remainingBreadReward: 0,
            recordScore: performanceGame.gameRecord.score,
            recordDuration: performanceGame.gameRecord.durationInMs
              ? getTimeString(
                  performanceGame.gameRecord.durationInMs,
                  'HH:mm:ss.SS',
                  true,
                )
              : undefined,
            isUnlocked: true,
            aiRevisionStatus: undefined,
            onClickPlay: () => {
              const gameLinks = gameLinkList.filter(
                (link) =>
                  link !==
                  `/club/${performanceGame.gameRecord.classroomId}/journey/${performanceGame.gameRecord.gameSetId}/game/${performanceGame.gameRecord.gameId}`,
              );
              useSessionStore.getState().setGameListsData({
                type: GameListsType.Performance,
                gameLinkList: gameLinks,
                totalGames: gameLinks.length,
              });
              // useRecordStore
              //   .getState()
              //   .moveInAlbumSummaryPath(
              //     performanceGame.gameRecord.classroomId,
              //     performanceGame.gameRecord.gameSetId,
              //     index,
              //   )
              //   .catch(console.error);
              navigate(
                `/club/${performanceGame.gameRecord.classroomId}/journey/${performanceGame.gameRecord.gameSetId}/game/${performanceGame.gameRecord.gameId}`,
              );
            },
            onClickAnalysis: () => {
              const gameLinks = gameLinkList.filter(
                (link) =>
                  link !==
                  `/club/${performanceGame.gameRecord.classroomId}/journey/${performanceGame.gameRecord.gameSetId}/game/${performanceGame.gameRecord.gameId}`,
              );
              useSessionStore.getState().setGameListsData({
                type: GameListsType.Performance,
                gameLinkList: gameLinks,
                totalGames: gameLinks.length,
              });
              navigate(
                `/club/${performanceGame.gameRecord.classroomId}/journey/${performanceGame.gameRecord.gameSetId}/game/${performanceGame.gameRecord.gameId}/analysis`,
              );
            },
            onClickInfo: () => {
              const popupInfoType = stringToGameTypeHelpType(
                gameItem?.icon?.tag,
              );
              popupInfoType && onClickInfo(popupInfoType);
            },
          },
        };
      });
    }
    return [];
  }, [navigate, onClickInfo, performance, performanceGames, userProfile]);

  const onClickBack = useCallback(() => {
    if (navigationState?.withBackState) {
      navigate(-1);
    } else {
      navigate(`/club/${classroomId}/performance-report`, {
        state: { selectedPeriod: periodType },
      });
    }
  }, [classroomId, navigate, navigationState?.withBackState, periodType]);

  return (
    <>
      <GameListTemplateV2
        title={getStarsByStar(Number(performance))?.text ?? ''}
        stations={stationsData}
        hasCursor={hasCursor}
        onClickBack={onClickBack}
        loadMoreItems={loadMoreItems}
      />
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};

export default PerformanceReportGameList;
